import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, type Zone, type ZoneInput } from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, Badge, type Column } from '@/components/ui';
import { IconMap } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader } from '@/components/admin/shared';
import { Drawer, Field } from '@/components/admin/Drawer';
import { useAdminPermission } from '@/lib/useAdminPermission';

/** Service zones — the CIRCLE service areas (content-svc migration 054) that gate
 *  bookings + go-online. A zone is a centre (lat/lng) + a radius in km; "in
 *  service" = inside ANY active zone. The rider/driver apps read these from
 *  app-settings to show the "service isn't available in your area yet" dialog, and
 *  trip-svc + driver-svc enforce them server-side.
 *
 *  AUTHORED here by staff with cms.manage (ops + superadmin): create / edit /
 *  activate / delete a zone (admin-svc proxies the write to content-svc, which
 *  owns the table; audited + RBAC-gated). Read-only roles still see the list.
 *  DEFENSIVE: shows a "not yet available" state when content-svc isn't reachable. */
export function Zones() {
  const [editing, setEditing] = useState<Zone | 'new' | null>(null);
  const canManage = useAdminPermission('cms.manage');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'zones'],
    queryFn: ({ signal }) => adminApi.zones(signal),
  });

  const unavailable = data && data.available === false;

  return (
    <AdminPage>
      <AdminPageHeader
        title="Service zones"
        subtitle="Circle service areas the apps + dispatch gate on (riders can't book / drivers can't go online outside an active zone)."
      />

      {unavailable ? (
        <Card>
          <EmptyState
            title="Service zones not yet available"
            description={
              data?.note ??
              'The service_zones table is owned by content-svc; admin-svc is not configured to reach it yet.'
            }
            icon={<IconMap className="h-6 w-6" />}
          />
        </Card>
      ) : (
        <>
          {canManage ? (
            <div className="mb-3 flex justify-end">
              <button type="button" onClick={() => setEditing('new')} className="btn-primary">
                New zone
              </button>
            </div>
          ) : null}

          <Card flush>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner label="Loading zones…" />
              </div>
            ) : isError ? (
              <EmptyState title="Couldn't load zones" description={(error as Error)?.message} />
            ) : (
              <ZonesTable rows={data?.items ?? []} canManage={canManage} onEdit={(z) => setEditing(z)} />
            )}
          </Card>
        </>
      )}

      {editing ? (
        <ZoneDrawer zone={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      ) : null}
    </AdminPage>
  );
}

/** The zones DataTable. Includes an inline activate/deactivate toggle (a full
 *  re-save with `active` flipped) + an Edit button for cms.manage roles. */
function ZonesTable({
  rows,
  canManage,
  onEdit,
}: {
  rows: Zone[];
  canManage: boolean;
  onEdit: (z: Zone) => void;
}) {
  const qc = useQueryClient();
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const toggle = useMutation({
    // No partial endpoint: re-save the whole row with `active` flipped.
    mutationFn: ({ zone, active }: { zone: Zone; active: boolean }) =>
      adminApi.updateZone(zone.id, {
        name: zone.name,
        active,
        center_lat: zone.center_lat,
        center_lng: zone.center_lng,
        radius_km: zone.radius_km,
      }),
    onMutate: ({ zone }) => setTogglingId(zone.id),
    onSettled: () => {
      setTogglingId(null);
      qc.invalidateQueries({ queryKey: ['admin', 'zones'] });
    },
  });

  const columns: Array<Column<Zone>> = [
    { key: 'id', header: 'ID', cell: (r) => <span className="metric text-xs text-slate-400">#{r.id}</span> },
    { key: 'name', header: 'Name', cell: (r) => <span className="font-medium text-slate-100">{r.name}</span> },
    {
      key: 'centre',
      header: 'Centre',
      cell: (r) => (
        <span className="metric text-xs text-slate-300">
          {r.center_lat.toFixed(4)}, {r.center_lng.toFixed(4)}
        </span>
      ),
    },
    {
      key: 'radius',
      header: 'Radius',
      cell: (r) => <span className="text-xs text-slate-300">{r.radius_km} km</span>,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => (
        <Badge tone={r.active ? 'ok' : 'neutral'} dot>
          {r.active ? 'active' : 'inactive'}
        </Badge>
      ),
    },
  ];
  if (canManage) {
    columns.push({
      key: 'actions',
      header: '',
      align: 'right',
      cell: (r) => (
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            disabled={toggle.isPending && togglingId === r.id}
            onClick={() => toggle.mutate({ zone: r, active: !r.active })}
            className="rounded-md border border-ink-700 px-2 py-1 text-xs text-slate-300 hover:bg-ink-800 disabled:opacity-50"
          >
            {toggle.isPending && togglingId === r.id ? '…' : r.active ? 'Deactivate' : 'Activate'}
          </button>
          <button
            type="button"
            onClick={() => onEdit(r)}
            className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
          >
            Edit
          </button>
        </div>
      ),
    });
  }

  return (
    <DataTable<Zone>
      columns={columns}
      rows={rows}
      rowKey={(r) => String(r.id)}
      stickyHeader
      empty={<EmptyState title="No zones" description="No service zones yet — add one to gate the launch area." icon={<IconMap className="h-6 w-6" />} />}
    />
  );
}

// ─── zone authoring drawer ───────────────────────────────────

/** The editable form state. Numbers bind as strings (parsed on submit). */
interface FormState {
  name: string;
  active: boolean;
  center_lat: string;
  center_lng: string;
  radius_km: string;
}

/** ZoneDrawer — the create/edit form. zone=null ⇒ create; otherwise edit. */
function ZoneDrawer({ zone, onClose }: { zone: Zone | null; onClose: () => void }) {
  const isEdit = zone !== null;
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(() => seedForm(zone));
  const [clientError, setClientError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (input: ZoneInput) => (isEdit ? adminApi.updateZone(zone!.id, input) : adminApi.createZone(input)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'zones'] });
      onClose();
    },
  });

  const del = useMutation({
    mutationFn: () => adminApi.deleteZone(zone!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'zones'] });
      onClose();
    },
  });

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  const onSubmit = () => {
    setClientError(null);
    const built = buildInput(form);
    if ('error' in built) {
      setClientError(built.error);
      return;
    }
    mutation.mutate(built.input);
  };

  const busy = mutation.isPending || del.isPending;

  return (
    <Drawer
      title={isEdit ? `Edit zone #${zone!.id}` : 'New zone'}
      subtitle={isEdit ? zone!.name : 'Define a circle service area'}
      onClose={onClose}
    >
      <div className="space-y-4">
        {isEdit ? (
          <div>
            <Field label="ID" value={`#${zone!.id}`} />
            <Field label="Status" value={zone!.active ? 'Active' : 'Inactive'} />
          </div>
        ) : null}

        <Card>
          <div className="space-y-3">
            <TextField
              label="Name"
              hint="The zone name staff see (e.g. Sydney CBD)."
              value={form.name}
              onChange={(v) => set('name', v)}
              placeholder="Sydney CBD"
            />
            <div className="grid grid-cols-2 gap-3">
              <TextField
                label="Centre latitude"
                hint="-90 to 90"
                value={form.center_lat}
                onChange={(v) => set('center_lat', v)}
                type="number"
                placeholder="-33.8688"
              />
              <TextField
                label="Centre longitude"
                hint="-180 to 180"
                value={form.center_lng}
                onChange={(v) => set('center_lng', v)}
                type="number"
                placeholder="151.2093"
              />
            </div>
            <TextField
              label="Radius (km)"
              hint="How far from the centre the service extends."
              value={form.radius_km}
              onChange={(v) => set('radius_km', v)}
              type="number"
              placeholder="15"
            />

            <label className="flex items-center gap-2 pt-1 text-sm text-slate-200">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => set('active', e.target.checked)}
                className="h-4 w-4 rounded border-ink-600 bg-ink-900"
              />
              Active (enforced now — riders + drivers gated to this zone)
            </label>
          </div>
        </Card>

        <div className="flex items-center gap-3">
          <button type="button" disabled={busy} onClick={onSubmit} className="btn-primary">
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create zone'}
          </button>
          <button type="button" disabled={busy} onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          {isEdit ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                if (window.confirm('Delete this zone? This cannot be undone.')) del.mutate();
              }}
              className="ml-auto rounded-md border border-danger/40 px-3 py-1.5 text-xs text-danger hover:bg-danger/10 disabled:opacity-50"
            >
              {del.isPending ? 'Deleting…' : 'Delete'}
            </button>
          ) : null}
        </div>
        {clientError ? <p className="text-xs text-danger">{clientError}</p> : null}
        {mutation.isError ? <p className="text-xs text-danger">{(mutation.error as Error)?.message}</p> : null}
        {del.isError ? <p className="text-xs text-danger">{(del.error as Error)?.message}</p> : null}
      </div>
    </Drawer>
  );
}

/** A labelled text/number input row used in the zone form. */
function TextField({
  label,
  hint,
  value,
  onChange,
  type = 'text',
  placeholder,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'number';
  placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-[11px] text-muted">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 input"
      />
      {hint ? <p className="mt-0.5 text-[11px] text-muted">{hint}</p> : null}
    </div>
  );
}

/** Seed the form from a zone (edit) or empty defaults (create). */
function seedForm(z: Zone | null): FormState {
  if (!z) {
    return { name: '', active: true, center_lat: '', center_lng: '', radius_km: '' };
  }
  return {
    name: z.name,
    active: z.active,
    center_lat: String(z.center_lat),
    center_lng: String(z.center_lng),
    radius_km: String(z.radius_km),
  };
}

/** Validate + convert the form into a ZoneInput, or return an error string.
 *  Mirrors content-svc validation so the staffer sees a precise message
 *  instantly; the server re-checks everything (this is a UX nicety). */
function buildInput(f: FormState): { input: ZoneInput } | { error: string } {
  const name = f.name.trim();
  if (!name) return { error: 'Name is required.' };

  const lat = Number(f.center_lat.trim());
  const lng = Number(f.center_lng.trim());
  const rad = Number(f.radius_km.trim());

  if (f.center_lat.trim() === '' || !Number.isFinite(lat) || lat < -90 || lat > 90) {
    return { error: 'Centre latitude must be a number between -90 and 90.' };
  }
  if (f.center_lng.trim() === '' || !Number.isFinite(lng) || lng < -180 || lng > 180) {
    return { error: 'Centre longitude must be a number between -180 and 180.' };
  }
  if (f.radius_km.trim() === '' || !Number.isFinite(rad) || rad <= 0 || rad > 500) {
    return { error: 'Radius (km) must be greater than 0 and at most 500.' };
  }

  return { input: { name, active: f.active, center_lat: lat, center_lng: lng, radius_km: rad } };
}
